import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { MapPin, Flame, Trophy, Vote, Store, HelpCircle, Radio, Crown, Info, Sparkles, Landmark, Heart, KeyRound, PartyPopper, Megaphone, Tv, Fuel } from "lucide-react";
import SponsorSpotlight from "@/components/SponsorSpotlight";
import CommunityTankSection from "@/components/CommunityTankSection";
import SponsorCTA from "@/components/SponsorCTA";
import DailyQuoteBanner from "@/components/DailyQuoteBanner";
import TeaserTicker from "@/components/TeaserTicker";
import FounderCard from "@/components/FounderCard";
import HuntZoneMap from "@/components/HuntZoneMap";
import { PrizeTypeBadge } from "@/components/PrizeDisplay";
import NextClueChip from "@/components/NextClueChip";
import ExclusiveHuntPills from "@/components/ExclusiveHuntPills";

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return <span className="font-mono text-[#76B82A]">LIVE NOW</span>;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <span className="font-mono text-2xl md:text-3xl" style={{ color: "#D7A62A" }}>
      {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

export default function HomePage() {
  const [live, setLive] = useState(null);
  const [drops, setDrops] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [online, setOnline] = useState(null);

  useEffect(() => {
    (async () => {
      const [l, d, n] = await Promise.all([
        api.get("/drops/live"),
        api.get("/drops"),
        api.get("/notifications", { params: { limit: 8 } }),
      ]);
      setLive(l.data.drop);
      setDrops(d.data.drops || []);
      setNotifs(n.data.notifications || []);
    })();
  }, []);

  // Live online ticker — refreshes every 30s
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/presence/online-count");
        if (!cancelled) setOnline(data.online ?? 0);
      } catch {
        // silent
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const nextScheduled = drops.find((d) => d.status === "scheduled");

  return (
    <div className="py-6 md:py-10 space-y-10">
      {/* Live banner */}
      {live && (
        <div
          className="border border-[#E53935]/50 -mx-4 md:-mx-8"
          style={{ background: "rgba(229,57,53,0.06)" }}
          data-testid="live-banner"
        >
          <Marquee gradient={false} speed={45} pauseOnHover>
            <span className="font-display text-4xl md:text-6xl uppercase py-3 px-8 tracking-tight">
              <span className="text-[#E53935]">● DROP LIVE IN {live.city}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
              <span className="text-[#D7A62A]">{live.mystery_amount ? "MYSTERY LOOT" : `$${live.amount}`}</span>
              <span className="text-[#F5F2E8]/40"> // </span>
              <span className="text-[#F5F2E8]">FOLLOW THE CLUES</span>
              <span className="text-[#F5F2E8]/40"> // </span>
            </span>
          </Marquee>
        </div>
      )}

      {/* Hero */}
      <section className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 loot-card-flat p-6 md:p-10">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {live ? (
              <>
                <span className="live-dot" aria-hidden="true" />
                <span className="overline text-[#E53935]">Drop live now</span>
              </>
            ) : (
              <>
                <Radio size={14} className="text-[#76B82A]" aria-hidden="true" />
                <span className="overline">No drop live</span>
              </>
            )}
            {online !== null && online > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 ml-2 border border-[#76B82A]/40 bg-[#76B82A]/10 rounded-sm"
                data-testid="online-counter"
                title="Hunters with the app open right now"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#76B82A] animate-pulse" />
                <span className="font-mono text-[11px] text-[#76B82A] uppercase tracking-wider">
                  {online} {online === 1 ? "hunter" : "hunters"} online
                </span>
              </span>
            )}
          </div>
          <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
            {live ? (
              <>
                {live.city} <span style={{ color: "#76B82A" }}>is hunting.</span>
              </>
            ) : (
              <>
                Stay ready.<br />
                <span style={{ color: "#D7A62A" }}>Pay attention.</span>
              </>
            )}
          </h1>
          <p className="mt-4 text-[#F5F2E8]/70 max-w-lg">
            {live
              ? live.sponsor
                ? `Sponsored by ${live.sponsor.business_name}. First finder keeps the prize.`
                : "Funded by The Local Loot. First finder keeps the prize."
              : "We hide real rewards — cash, gift cards, merch, experiences. We post clues. You find it. You keep it."}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {live && (
              <Link to={`/drops/${live.id}`} data-testid="home-view-live-btn" className="btn-loot inline-flex items-center gap-2">
                <Flame size={16} /> View live clues
              </Link>
            )}
            <Link to="/winners" data-testid="home-winners-btn" className="btn-ghost inline-flex items-center gap-2">
              <Trophy size={16} /> Winners wall
            </Link>
            <Link to="/sponsor-apply" data-testid="home-sponsor-btn" className="btn-ghost inline-flex items-center gap-2">
              <Store size={16} /> Sponsor a drop
            </Link>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="loot-card-flat p-6">
            <div className="overline mb-2">Next drop in</div>
            {nextScheduled ? (
              <>
                <Countdown target={nextScheduled.launch_time} />
                <div className="mt-3 flex items-center gap-2 text-[#F5F2E8]/80">
                  <MapPin size={14} /> {nextScheduled.city}
                </div>
                <div className="mt-1 text-[#F5F2E8]/50 text-sm">{nextScheduled.title}</div>
              </>
            ) : (
              <div className="font-mono text-[#F5F2E8]/60">No upcoming drop</div>
            )}
          </div>

          <HuntZoneMap
            zone={live?.zone}
            height={160}
            label="Hunt zone"
            sub={live?.zone_description || (live?.zone ? "City-level preview" : "Blurred until live")}
          />
        </div>
      </section>

      {/* Sponsor spotlight — paying sponsors get prime real estate, right
          under the hero. Auto-rotating carousel; renders nothing if zero
          active sponsors. */}
      <SponsorSpotlight />

      {/* Community LootFund — people-powered prize pool. Renders nothing if
          admin has the toggle off. */}
      <CommunityTankSection variant="dark" />

      {/* FOMO ticker — next drop teaser with watcher count */}
      <TeaserTicker />

      {/* Exclusive private hunts — only renders if a host opted in to public visibility */}
      <ExclusiveHuntPills />

      <FounderCard />

      {/* Daily quote — AI/admin hybrid, "Daily Signal" under hero */}
      <DailyQuoteBanner />

      {/* Sponsor CTA — convert businesses from viewers into sponsors */}
      <SponsorCTA />

      {/* Quick actions */}
      <section>
        <div className="overline mb-3">Quick actions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { to: "/drops", icon: Flame, label: "Upcoming drops", testid: "qa-drops" },
            { to: "/missions", icon: Heart, label: "Community missions", testid: "qa-missions", accent: "#76B82A" },
            { to: "/community-tank", icon: Fuel, label: "Community Tank", testid: "qa-tank", accent: "#76B82A" },
            { to: "/lootmaster", icon: KeyRound, label: "Loot Master", testid: "qa-lootmaster", accent: "#D7A62A" },
            { to: "/private/sales", icon: PartyPopper, label: "Host a private hunt", testid: "qa-private", accent: "#D7A62A" },
            { to: "/sponsor-a-mission", icon: Megaphone, label: "Sponsor a mission", testid: "qa-sponsor-mission", accent: "#76B82A" },
            { to: "/waymarks", icon: Landmark, label: "Waymarks", testid: "qa-waymarks" },
            { to: "/loot-quest", icon: Sparkles, label: "Loot Quest", testid: "qa-loot-quest" },
            { to: "/winners", icon: Trophy, label: "Winners", testid: "qa-winners" },
            { to: "/vote", icon: Vote, label: "Vote your city", testid: "qa-vote" },
            { to: "/sponsors", icon: Store, label: "Sponsors", testid: "qa-sponsors" },
            { to: "/", icon: Tv, label: "About the Loot", testid: "qa-splash" },
            { to: "/how-it-works", icon: HelpCircle, label: "How it works", testid: "qa-how" },
          ].map(({ to, icon: Icon, label, testid, accent }) => (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className="loot-card p-4 flex flex-col items-start gap-2"
            >
              <Icon size={22} style={{ color: accent || "#76B82A" }} />
              <span className="font-display uppercase tracking-tight">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Drops list + activity */}
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="overline">All drops</div>
            <Link to="/vote" className="font-mono text-xs text-[#76B82A]">Request a city →</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {drops.length === 0 && (
              <div className="loot-card-flat p-6 text-[#F5F2E8]/60">No drops scheduled yet.</div>
            )}
            {drops.map((d) => (
              <Link
                key={d.id}
                to={`/drops/${d.id}`}
                data-testid={`drop-card-${d.id}`}
                className="loot-card p-5 block relative"
                style={d.is_golden ? {
                  borderColor: "#D7A62A",
                  background: "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 60%)",
                } : undefined}
              >
                {d.is_golden && (
                  <Crown
                    size={18}
                    aria-hidden="true"
                    className="absolute top-3 right-3"
                    style={{ color: "#D7A62A" }}
                  />
                )}
                {d.boosted && (
                  <span
                    className="absolute top-3 left-3 tag-pill text-[10px]"
                    style={{ background: "#E53935", color: "#F5F2E8", borderColor: "#E53935" }}
                    data-testid={`boosted-${d.id}`}
                  >
                    ⚡ BOOSTED
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <span
                    className="tag-pill"
                    style={{
                      borderColor:
                        d.is_golden ? "#D7A62A" :
                        d.status === "live" ? "#E53935" :
                        d.status === "found" ? "#D7A62A" : "#76B82A",
                      color:
                        d.is_golden ? "#D7A62A" :
                        d.status === "live" ? "#E53935" :
                        d.status === "found" ? "#D7A62A" : "#76B82A",
                    }}
                  >
                    {d.is_golden ? "Golden" : ""} {d.status}
                  </span>
                  <span className="font-mono text-xs text-[#F5F2E8]/50">
                    {d.mystery_amount ? "MYSTERY LOOT" : `$${d.amount}`}
                  </span>
                </div>
                <div className="mt-1">
                  <PrizeTypeBadge drop={d} />
                </div>
                <h3 className="font-display text-2xl uppercase mt-3 tracking-tight">{d.title}</h3>
                <div className="mt-2 flex items-center gap-2 text-sm text-[#F5F2E8]/70">
                  <MapPin size={14} />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/hunts/${encodeURIComponent(d.city)}`; }}
                    className="hover:text-[#76B82A]"
                  >
                    {d.city}
                  </button>
                </div>
                <div className="mt-2">
                  <NextClueChip drop={d} />
                </div>
                <div className="mt-3 font-mono text-xs text-[#F5F2E8]/50">
                  {d.sponsor ? `Sponsored by ${d.sponsor.business_name}` : "Funded by The Local Loot"}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div className="overline mb-3">Activity feed</div>
          <div className="loot-card-flat divide-y divide-[#F5F2E8]/10">
            {notifs.length === 0 && (
              <div className="p-4 text-[#F5F2E8]/50 text-sm">No recent activity.</div>
            )}
            {notifs.map((n) => (
              <div key={n.id} className="p-4">
                <div className="flex items-center gap-2">
                  <span
                    className="tag-pill"
                    style={{
                      color:
                        n.tag === "winner" ? "#D7A62A" :
                        n.tag === "drop" ? "#E53935" : "#76B82A",
                      borderColor:
                        n.tag === "winner" ? "#D7A62A" :
                        n.tag === "drop" ? "#E53935" : "#76B82A",
                    }}
                  >
                    {n.tag}
                  </span>
                  {n.city && <span className="font-mono text-[10px] text-[#F5F2E8]/50">{n.city}</span>}
                </div>
                <div className="font-display uppercase mt-2 text-lg leading-tight">{n.title}</div>
                <div className="text-sm text-[#F5F2E8]/70 mt-1">{n.message}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
